import React from 'react';
import { Menu, User, LogOut, MapPin, ChevronDown, ChevronRight, Check, Layers3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { useNavigate, useLocation as useRouteLocation } from 'react-router-dom';
import NotificationBell from '../ui/NotificationBell';
import Button from '../ui/Button';

interface HeaderProps {
  onMenuClick: () => void;
}

const pageNames: Record<string, string> = {
  'education': 'Campus Intelligence',
  dashboard: 'Overview', locations: 'Locations', cameras: 'Cameras', live: 'Live View',
  events: 'Events', actions: 'Actions', 'sensors-devices': 'Sensors & Devices', 'entry-exit': 'Entry / Exit', persons: 'Person Registry',
  parking: 'Parking', tenants: 'Tenants', company: 'Company', 'ai-search': 'AI Search',
  configuration: 'Configuration', profile: 'Profile',
};

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { logout } = useAuth();
  const { locations, selectedLocationId, setSelectedLocationId, isLoading: locationsLoading } = useLocation();
  const navigate = useNavigate();
  const { pathname } = useRouteLocation();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showLocationMenu, setShowLocationMenu] = React.useState(false);
  const locationButton = React.useRef<HTMLButtonElement>(null);
  const accountButton = React.useRef<HTMLButtonElement>(null);
  const locationName = selectedLocationId === 'all' ? 'All Locations'
    : locations.find(location => location.ycl_id?.toString() === selectedLocationId)?.ycl_name || 'All Locations';

  React.useEffect(() => {
    if (!showUserMenu && !showLocationMenu) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showLocationMenu) locationButton.current?.focus();
      if (showUserMenu) accountButton.current?.focus();
      setShowUserMenu(false);
      setShowLocationMenu(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [showLocationMenu, showUserMenu]);

  const selectLocation = (id: string) => {
    setSelectedLocationId(id);
    setShowLocationMenu(false);
    locationButton.current?.focus();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header style={{ backgroundColor: 'var(--header-bg)' }} className="h-16 shrink-0 border-b border-border-default relative z-30">
      <div className="h-full px-4 lg:px-6 flex items-center gap-3">
        <Button type="button" variant="ghost" onClick={onMenuClick} aria-label="Open navigation" className="lg:hidden -ml-2">
          <Menu size={21} />
        </Button>
        <div className="hidden md:flex min-w-0 items-center gap-2 text-xs text-text-muted">
          <Layers3 size={16} />
          <span>Workspace</span>
          <ChevronRight size={13} />
          <span className="font-medium text-text-primary truncate">{pageNames[pathname.split('/')[1]] || 'Workspace'}</span>
        </div>
        <div className="relative min-w-0 flex-1 md:flex-none md:ml-auto">
          <Button type="button" variant="outline" ref={locationButton}
            onClick={() => { setShowLocationMenu(!showLocationMenu); setShowUserMenu(false); }}
            aria-expanded={showLocationMenu} aria-controls="workspace-locations" title="Switch location"
            className="w-full md:w-56"
            disabled={locationsLoading}>
            <MapPin size={16} className="text-accent-primary shrink-0" />
            <span className="truncate flex-1 text-left">{locationsLoading ? 'Loading...' : locationName}</span>
            <ChevronDown size={14} className={`text-text-muted shrink-0 transition-transform ${showLocationMenu ? 'rotate-180' : ''}`} />
          </Button>
          {showLocationMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowLocationMenu(false)} />
              <div id="workspace-locations" className="dropdown-panel fixed left-4 right-4 top-[60px] md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-80 z-50 max-h-[min(420px,70dvh)] overflow-y-auto">
                <p className="px-2.5 pt-1.5 pb-2 text-[10px] font-medium uppercase tracking-widest text-text-muted">Switch location</p>
                <button type="button" onClick={() => selectLocation('all')}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-left transition-colors ${selectedLocationId === 'all' ? 'bg-accent-primary/10 text-accent-primary' : 'text-text-secondary hover:bg-surface-hover'}`}>
                  <Layers3 size={16} /><span className="text-[13px] font-medium flex-1">All Locations</span>
                  {selectedLocationId === 'all' && <Check size={15} />}
                </button>
                <div className="border-t border-border-default my-1.5" />
                {locations.map(location => (
                  <button type="button" key={location.ycl_id} onClick={() => selectLocation(location.ycl_id?.toString() || 'all')}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-left transition-colors ${selectedLocationId === location.ycl_id?.toString() ? 'bg-accent-primary/10 text-accent-primary' : 'text-text-secondary hover:bg-surface-hover'}`}>
                    <MapPin size={16} className="shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="text-[13px] font-medium truncate block">{location.ycl_name}</span>
                      {location.ycl_address && <span className="text-xs text-text-muted truncate block mt-0.5">{location.ycl_address}</span>}
                    </span>
                    {selectedLocationId === location.ycl_id?.toString() && <Check size={15} className="shrink-0" />}
                  </button>
                ))}
                {locations.length === 0 && <p className="text-xs text-text-muted text-center py-4">No locations found</p>}
              </div>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <NotificationBell />
          <div className="h-6 border-l border-border-default" />
          <div className="relative">
            <Button type="button" variant="ghost" ref={accountButton} aria-label="My account" aria-expanded={showUserMenu} aria-controls="account-menu"
              onClick={() => { setShowUserMenu(!showUserMenu); setShowLocationMenu(false); }}>
              <User size={20} />
              <span className="hidden xl:block">My account</span>
              <ChevronDown size={13} className="hidden sm:block text-text-muted" />
            </Button>
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div id="account-menu" className="dropdown-panel absolute right-0 mt-3 w-48 z-50">
                  <button type="button" onClick={() => { setShowUserMenu(false); navigate('/profile'); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-text-secondary hover:text-text-primary hover:bg-surface-hover rounded-lg text-left text-[13px]">
                    <User size={16} />Profile
                  </button>
                  <div className="border-t border-border-default my-1" />
                  <button type="button" onClick={() => { setShowUserMenu(false); handleLogout(); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-status-danger hover:bg-status-danger/10 rounded-lg text-left text-[13px]">
                    <LogOut size={16} />Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
